import writeXlsxFile, { type Column } from "write-excel-file/browser";
import moment from "moment";

export function objectToQueryString(obj: any) {
  let queryString = "";
  for (let key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const value = obj[key];
    if (value === undefined || value === null || value === "") continue;

      if (queryString.length > 0) {
        queryString += "&";
      }

      // if(key === 'pageNumber') {
      //   queryString += "page=" + encodeURIComponent(obj[key] -1 );
      // }

      // if(key === 'pageSize') {
      //   queryString += "limit=" + encodeURIComponent(obj[key] -1 );
      // }

      if (key === "page") {
        queryString += key + "=" + encodeURIComponent(value - 1);
      } else {
        queryString += key + "=" + encodeURIComponent(value);
      }
  }
  return queryString;
}

/** True for non-null plain objects (not arrays, Date, or RegExp). */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") return false;
  if (Array.isArray(value)) return false;
  if (value instanceof Date) return false;
  const proto = Object.getPrototypeOf(value as object);
  return proto === Object.prototype || proto === null;
}

export const convertTo12HourFormat = (inputValue) => {
  const [hours, minutes] = inputValue.split(":");

  let twelveHourFormat: string;
  if (hours >= 12) {
    if (hours > 12) {
      twelveHourFormat = `${hours - 12}:${minutes} PM`;
    } else {
      twelveHourFormat = `${hours}:${minutes} PM`;
    }
  } else {
    if (hours === "00") {
      twelveHourFormat = `12:${minutes} AM`;
    } else {
      twelveHourFormat = `${hours}:${minutes} AM`;
    }
  }

  return twelveHourFormat;
};

// const formattedDate = moment(arg).format('MM/DD/YYYY');

/** Parse mixed Mongo / ISO / YYYYMMDD date values into a moment (invalid → null). */
function parseExportMoment(arg: unknown): moment.Moment | null {
  if (arg == null || arg === "") return null;
  let raw: unknown = unwrapSpreadsheetCell(arg);
  if (isPlainObject(raw) && "$date" in (raw as object)) {
    raw = (raw as { $date: unknown }).$date;
  }
  if (isPlainObject(raw) && raw != null && "$numberLong" in (raw as object)) {
    raw = Number((raw as { $numberLong: unknown }).$numberLong);
  }
  if (raw instanceof Date) {
    const m = moment(raw);
    return m.isValid() ? m : null;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const s = String(Math.trunc(raw));
    if (/^\d{8}$/.test(s)) {
      const m = moment(s, "YYYYMMDD", true);
      return m.isValid() ? m : null;
    }
    // epoch ms vs seconds
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const m = moment(ms);
    return m.isValid() ? m : null;
  }
  const s = String(raw).trim();
  if (!s || /^null$/i.test(s) || /^undefined$/i.test(s) || /^none$/i.test(s)) {
    return null;
  }
  if (/^\d{8}$/.test(s)) {
    const m = moment(s, "YYYYMMDD", true);
    return m.isValid() ? m : null;
  }
  if (/^\d{10,13}$/.test(s)) {
    const n = Number(s);
    const ms = n < 1e12 ? n * 1000 : n;
    const m = moment(ms);
    return m.isValid() ? m : null;
  }

  const formats = [
    moment.ISO_8601,
    "YYYY-MM-DDTHH:mm:ss.SSSZ",
    "YYYY-MM-DDTHH:mm:ssZ",
    "YYYY-MM-DDTHH:mm:ss.SSS",
    "YYYY-MM-DDTHH:mm:ss",
    "YYYY-MM-DD HH:mm:ss.SSS",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "YYYY/MM/DD HH:mm:ss",
    "YYYY/MM/DD",
    "MM/DD/YYYY h:mm:ss A",
    "MM/DD/YYYY hh:mm:ss A",
    "M/D/YYYY h:mm:ss A",
    "M/D/YYYY h:mm A",
    "MM/DD/YYYY H:mm:ss",
    "MM/DD/YYYY HH:mm:ss",
    "MM/DD/YYYY",
    "M/D/YYYY",
    "YYYY-MM-DD",
    "MMM D YYYY",
    "MMM DD YYYY",
    "MMMM D, YYYY",
  ];
  const strict = moment(s, formats, true);
  if (strict.isValid()) return strict;
  const loose = moment(s);
  return loose.isValid() ? loose : null;
}

/** US date only — e.g. 09/07/2026 */
export const formatDate = (arg: unknown) => {
  const m = parseExportMoment(arg);
  if (!m) return "";
  return m.format("MM/DD/YYYY");
};

/** US date + time for UI / spreadsheet exports — e.g. 09/07/2026 2:15:03 PM */
export const formatDateTime = (arg: unknown) => {
  const m = parseExportMoment(arg);
  if (!m) return "";
  return m.format("MM/DD/YYYY h:mm:ss A");
};

export const formatDateY = (date) => {
  if (date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(date.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }
};

export const getLastWeekDateRange = () => {
  const currentDate = new Date();

  // Calculate the end date, which is today (28th)
  const endDate = new Date(currentDate);

  // Calculate the start date, which is 6 days before the end date
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - 6);

  // Format the dates to YYYY-MM-DD
  const formatDate = (date) => date.toISOString().split("T")[0];

  return {
    start: formatDate(startDate),
    end: formatDate(endDate),
  };
};

/**
 * Unwrap `{ value: x }` cells (e.g. from spreadsheet imports / mixed pipelines).
 * Only unwraps plain objects whose sole own key is `value`, up to a few levels deep.
 */
function unwrapSpreadsheetCell(v: unknown): unknown {
  let cur: unknown = v;
  for (let i = 0; i < 8; i++) {
    if (cur == null || typeof cur !== "object" || Array.isArray(cur)) break;
    if (!isPlainObject(cur)) break;
    const o = cur as Record<string, unknown>;
    const keys = Object.keys(o);
    if (keys.length !== 1 || keys[0] !== "value") break;
    cur = o.value;
  }
  return cur;
}

/** Parse a scalar to a finite number for export formatting (numbers, numeric strings, common BSON). */
export function coerceToFiniteNumber(v: unknown): number | null {
  v = unwrapSpreadsheetCell(v);
  if (v == null || v === "") return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t);
    if (Number.isFinite(n)) return n;
    return null;
  }
  if (isPlainObject(v)) {
    const o = v;
    if (o.$numberDouble != null) {
      const n = Number(o.$numberDouble);
      return Number.isFinite(n) ? n : null;
    }
    if (o.$numberInt != null) {
      const n = Number(o.$numberInt);
      return Number.isFinite(n) ? n : null;
    }
    if (o.$numberLong != null) {
      const n = Number(o.$numberLong);
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
}

/** Serialize any cell for export — never rely on default String(object) → "[object Object]". */
function sanitizeExcelText(s: string): string {
  if (!s) return s;
  const t = s.trim();
  // Broken Sheets/Excel refs pasted into Mongo, or formula injection
  if (t === "#REF!" || t.includes("#REF!")) return "";
  if (t === "#N/A" || t === "#VALUE!" || t === "#NAME?" || t === "#DIV/0!") return "";
  if (/^[=+\-@\t\r\n]/.test(s)) {
    return `'${s}`;
  }
  return s;
}

function serializeExportCell(cell: unknown): string {
  if (cell === null || cell === undefined) return "";
  cell = unwrapSpreadsheetCell(cell);
  if (cell === null || cell === undefined) return "";
  if (typeof cell === "string") return sanitizeExcelText(cell);
  if (typeof cell === "number" || typeof cell === "boolean") return String(cell);
  if (cell instanceof Date) return cell.toISOString();
  if (Array.isArray(cell)) {
    try {
      return sanitizeExcelText(JSON.stringify(cell));
    } catch {
      return "";
    }
  }
  if (typeof cell === "object") {
    const anyCell = cell as { toString?: () => string };
    if (typeof anyCell.toString === "function") {
      const s = anyCell.toString();
      if (s && s !== "[object Object]") return sanitizeExcelText(s);
    }
    try {
      return sanitizeExcelText(JSON.stringify(cell));
    } catch {
      return "";
    }
  }
  return sanitizeExcelText(String(cell));
}

/**
 * Flatten nested plain objects into `parent_child` keys so spreadsheets get scalar columns.
 * Dates → ISO strings; arrays → JSON string; non-plain objects (e.g. ObjectId) → toString / JSON.
 */
export const flattenObject = (
  obj: Record<string, unknown> | null | undefined,
  parent = "",
  res: Record<string, unknown> = {}
): Record<string, unknown> => {
  if (obj == null || typeof obj !== "object") return res;

  for (const key of Object.keys(obj)) {
    const value = obj[key];
    const propName = parent ? `${parent}_${key}` : key;

    if (value instanceof Date) {
      res[propName] = value.toISOString();
      continue;
    }

    if (Array.isArray(value)) {
      res[propName] = serializeExportCell(value);
      continue;
    }

    if (value !== null && typeof value === "object") {
      if (isPlainObject(value)) {
        flattenObject(value as Record<string, unknown>, propName, res);
      } else {
        res[propName] = serializeExportCell(value);
      }
      continue;
    }

    res[propName] = value;
  }
  return res;
};

/** Safe date cell for spreadsheet export (empty when missing). */
export function formatExportDate(v: unknown): string {
  if (v == null || v === "") return "";
  const formatted = formatDate(v);
  if (formatted) return formatted;
  // Keep odd Mongo string dates visible instead of blanking the cell
  const raw = unwrapSpreadsheetCell(v);
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  return "";
}

/** Safe date+time cell for spreadsheet export. */
export function formatExportDateTime(v: unknown): string {
  if (v == null || v === "") return "";
  const formatted = formatDateTime(v);
  if (formatted) return formatted;
  const raw = unwrapSpreadsheetCell(v);
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw.toISOString();
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  return "";
}

/** Resolve a field from a row using canonical name + legacy aliases. */
export function resolveExportField(
  raw: Record<string, unknown>,
  flat: Record<string, unknown>,
  dbColName: string
): unknown {
  const aliases: Record<string, string[]> = {
    first_contact_created: [
      "first_contact_created",
      "first_contact_created_date",
    ],
    first_contact_plus_created: [
      "first_contact_plus_created",
      "first_contact_date",
      "first_contact_plus_date",
    ],
    first_signed_created: [
      "first_signed_created",
      "first_signed_date",
      "agreement_signed",
    ],
    first_signed_plus_created: [
      "first_signed_plus_created",
      "first_signed_plus_date",
      "completed_onboard_training",
    ],
    first_recruiter_created: [
      "first_recruiter_created",
      "first_recruiter_date",
      "first_recruiter",
    ],
    first_recruiter_plus_created: [
      "first_recruiter_plus_created",
      "first_recruiter_plus",
      "first_recruiter+",
    ],
    first_shpro_created: [
      "first_shpro_created",
      "first_sh_pro_date",
      "first_sh_pro_created",
      "last_solar_lead_date",
    ],
    first_shpro_plus_created: [
      "first_shpro_plus_created",
      "first_sh_pro_plus_date",
      "first_shpro_plus_date",
      "last_solar_install_date",
    ],
    createdAt: ["createdAt", "created_at"],
    updatedAt: ["updatedAt", "updated_at"],
    list_started_100: [
      "list_started_100",
      "100_list_started",
      "100 list started",
    ],
    list_completed_100: [
      "list_completed_100",
      "100_list_completed",
      "100 list completed",
    ],
  };

  const keys = aliases[dbColName] || [dbColName];
  for (const k of keys) {
    const v = raw[k] !== undefined ? raw[k] : flat[k];
    if (v != null && v !== "") return v;
  }
  return undefined;
}

/** Numeric cell for spreadsheet exports (BSON-safe); use with `decimals` in ExportColumnDef. */
export function parseExportNumber(v: unknown): number | null {
  const n = coerceToFiniteNumber(v);
  return n != null ? n : null;
}

export type ExportColumnDef = {
  dbColName: string;
  /** Spreadsheet header (defaults to dbColName) */
  title?: string;
  /**
   * If set, numeric values are rounded and shown with this many digits after the decimal (e.g. 1 → "1.4").
   * Non-numeric values still use default serialization.
   */
  decimals?: number;
  /** When used with `decimals`, leave the cell blank if the rounded value is exactly 0 */
  emptyIfZero?: boolean;
  /** Format date-like values for spreadsheet cells */
  format?: "date" | "datetime";
};

/**
 * Export rows to .xlsx. If `columns` is set, only those fields are included, in order, with the given headers.
 * Otherwise all flattened keys from the data are included (legacy behavior).
 */
export const exportToCSV = (
  apiData: unknown[],
  fileName: string,
  options?: { columns?: ExportColumnDef[] }
) => {
  const fileExtension = ".xlsx";

  type Row = Record<string, any>;

  const rowsIn = apiData || [];

  if (options?.columns?.length) {
    const titleUses = new Map<string, number>();
    const defs = options.columns.map((c, idx) => {
      // Spreadsheet header = table heading (`title`), not Mongo `dbColName`.
      const rawTitle = String(c.title ?? "").trim();
      const base =
        (rawTitle || String(c.dbColName ?? "").replace(/_/g, " ").trim()) ||
        c.dbColName;
      const n = (titleUses.get(base) || 0) + 1;
      titleUses.set(base, n);
      // Disambiguate duplicate headings without exposing Mongo field names.
      const header = n > 1 ? `${base} (${n})` : base;
      const dec = c.decimals;
      const decimals =
        typeof dec === "number" && dec >= 0 && Number.isFinite(dec)
          ? Math.min(20, Math.floor(dec))
          : undefined;
      return {
        dbColName: c.dbColName,
        header,
        idx,
        decimals,
        emptyIfZero: Boolean(c.emptyIfZero),
        format: c.format === "date" || c.format === "datetime" ? c.format : undefined,
      };
    });

    const flatRows: Row[] = rowsIn.map((item) => {
      const raw = normalizeExportRow(item);
      const flat = flattenObject(raw as Record<string, unknown>);
      const row: Row = {};
      defs.forEach((d) => {
        const key = `__c_${d.idx}`;
        let v = resolveExportField(raw, flat, d.dbColName);
        if (v === undefined) {
          v =
            raw[d.dbColName as keyof typeof raw] !== undefined
              ? raw[d.dbColName as keyof typeof raw]
              : flat[d.dbColName];
        }
        if (d.format === "datetime") {
          // Text + @ so Excel keeps US date+time (won't coerce to date-only).
          const text = sanitizeExcelText(formatExportDateTime(v));
          row[key] = text
            ? { type: String, value: text, format: "@" }
            : "";
        } else if (d.format === "date") {
          const text = sanitizeExcelText(formatExportDate(v));
          row[key] = text
            ? { type: String, value: text, format: "@" }
            : "";
        } else if (d.decimals != null) {
          const num = coerceToFiniteNumber(v);
          if (num != null) {
            const factor = 10 ** d.decimals;
            const rounded = Math.round(num * factor) / factor;
            if (d.emptyIfZero && rounded === 0) {
              row[key] = "";
            } else {
              row[key] = rounded.toFixed(d.decimals);
            }
          } else {
            const text = serializeExportCell(v);
            row[key] = text
              ? { type: String, value: text, format: "@" }
              : "";
          }
        } else {
          const text = serializeExportCell(v);
          row[key] = text
            ? { type: String, value: text, format: "@" }
            : "";
        }
      });
      return row;
    });

    const columns: Column<Row>[] = defs.map((d) => ({
      header: { value: d.header, fontWeight: "bold" as const },
      cell: (row: Row) => row[`__c_${d.idx}`] ?? "",
    }));

    void writeXlsxFile(flatRows, { columns })
      .toFile(fileName + fileExtension)
      .catch((e) => console.error(e));
    return;
  }

  const flattenedData: Row[] = rowsIn.map((item) =>
    flattenObject(
      item != null && typeof item === "object"
        ? (item as Record<string, unknown>)
        : ({} as Record<string, unknown>)
    )
  );

  const allKeys = new Set<string>();
  flattenedData.forEach((item) => {
    Object.keys(item || {}).forEach((key) => allKeys.add(key));
  });

  const headers = Array.from(allKeys);

  if (headers.length === 0) {
    void writeXlsxFile([[]])
      .toFile(fileName + fileExtension)
      .catch((e) => console.error(e));
    return;
  }

  const columns: Column<Row>[] = headers.map((header) => ({
    header,
    cell: (row: Row) => serializeExportCell(row[header]),
  }));

  void writeXlsxFile(flattenedData as Row[], { columns })
    .toFile(fileName + fileExtension)
    .catch((e) => console.error(e));
};

function escapeCsvField(value: string): string {
  const s = value == null ? "" : String(value);
  const escaped = s.replace(/"/g, '""');
  if (/[",\n\r]/.test(escaped)) return `"${escaped}"`;
  return escaped;
}

/** Normalize API row (Mongo document or ES hit) to a plain object for export. */
export function normalizeExportRow(item: unknown): Record<string, unknown> {
  if (item == null || typeof item !== "object") return {};
  const o = item as Record<string, unknown>;
  if (o._source != null && typeof o._source === "object") {
    return { ...(o._source as Record<string, unknown>) };
  }
  return { ...o };
}

/**
 * Build a UTF-8 CSV string from an array of records (nested objects flattened).
 * BOM prefix helps Excel open UTF-8 correctly.
 */
export function buildCsvFromObjects(rows: unknown[]): string {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const flat = rows.map((item) => flattenObject(normalizeExportRow(item)));
  const allKeys = new Set<string>();
  flat.forEach((row) => Object.keys(row || {}).forEach((k) => allKeys.add(k)));
  const headers = Array.from(allKeys);
  const bom = "\uFEFF";
  const headerLine = headers.map((h) => escapeCsvField(h)).join(",");
  const dataLines = flat.map((row) =>
    headers.map((h) => escapeCsvField(serializeExportCell(row[h]))).join(",")
  );
  return bom + [headerLine, ...dataLines].join("\r\n");
}

export const convertTimestamp = (ts: number) => {
  try {
    if (ts === null || ts === undefined || ts === 0) {
      return "";
    }

    const date = new Date(ts); // Convert to milliseconds

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(date.getDate()).padStart(2, "0");

    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  } catch (error) {
    return "";
  }

  // try {
  //   if (!ts.trim()) {
  //     return { success: false, error: 'Please enter a timestamp' };
  //   }

  //   const timestampNum: number = parseInt(ts, 10);

  //   if (isNaN(timestampNum)) {
  //     return { success: false, error: 'Invalid timestamp format' };
  //   }

  //   // Handle both seconds (10 digits) and milliseconds (13 digits)
  //   const date: Date = timestampNum.toString().length === 10
  //     ? new Date(timestampNum * 1000)  // Convert seconds to milliseconds
  //     : new Date(timestampNum);        // Already in milliseconds

  //   // Check if date is valid
  //   if (isNaN(date.getTime())) {
  //     return { success: false, error: 'Invalid timestamp value' };
  //   }

  //   // Format to American date format (MM/DD/YYYY, HH:MM:SS AM/PM)
  //   const options: Intl.DateTimeFormatOptions = {
  //     year: 'numeric',
  //     month: '2-digit',
  //     day: '2-digit',
  //     hour: '2-digit',
  //     minute: '2-digit',
  //     second: '2-digit',
  //     hour12: true,
  //     timeZone: 'America/New_York' // EST/EDT
  //   };

  //   const formattedDate: string = date.toLocaleString('en-US', options);
  //   return { success: true, date: formattedDate };

  // } catch (error) {
  //   return {
  //     success: false,
  //     error: error instanceof Error ? error.message : 'Unknown error occurred'
  //   };
  // }
};

export const validateTimeGap = (
  recruiting_interview,
  startTime,
  endTime,
  index
) => {
  if (!startTime || !endTime) return;

  // Parse times
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);

  // Calculate time difference in minutes
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;
  const diffMinutes = endTotalMinutes - startTotalMinutes;

  // Check if difference is at least 2 hours (120 minutes)
  if (diffMinutes > 120) {
    const updatedInterviews = [...recruiting_interview];
    updatedInterviews[index].end_time = endTime;

    return updatedInterviews;

    // Update with minimum valid end time
    // const updatedInterviews = [...state.recruiting_interview];
    // updatedInterviews[index].end_time = minEndTime;
  } else {
    // Calculate the minimum valid end time (2 hours after start time)
    let minEndHours = startHours + 2;
    const minEndMinutes = startMinutes;
    const minEndTime = `${String(minEndHours).padStart(2, "0")}:${String(
      minEndMinutes
    ).padStart(2, "0")}`;

    // Show notification to user
    alert(
      `The end time must be at least 2 hours after the start time. The minimum end time should be ${minEndTime}.`
    );
    const updatedInterviews = [...recruiting_interview];
    updatedInterviews[index].end_time = minEndTime;

    return updatedInterviews;
  }
};

const convertTimeToMinutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

export const hasTimeSlotConflict = (interviews) => {
  for (const interview of interviews) {
    const { date, start_time, end_time } = interview;

    if (!date || !start_time || !end_time) continue; // Skip incomplete entries

    // Convert times to minutes for easier comparison
    const startMinutes = convertTimeToMinutes(start_time);
    const endMinutes = convertTimeToMinutes(end_time);

    for (const existing of interviews) {
      if (existing === interview) continue; // Skip comparing the same interview

      const {
        date: existingDate,
        start_time: existingStartTime,
        end_time: existingEndTime,
      } = existing;

      if (date === existingDate) {
        const existingStartMinutes = convertTimeToMinutes(existingStartTime);
        const existingEndMinutes = convertTimeToMinutes(existingEndTime);

        // Check if the times overlap
        if (
          (startMinutes >= existingStartMinutes &&
            startMinutes < existingEndMinutes) || // New start_time is within an existing range
          (endMinutes > existingStartMinutes &&
            endMinutes <= existingEndMinutes) || // New end_time is within an existing range
          (startMinutes < existingStartMinutes &&
            endMinutes > existingEndMinutes) // New range completely overlaps an existing range
        ) {
          return `Interview session start and end time must not overlap if two or more BLOCK has same date`;
        }
      }
    }
  }

  return false; // No conflicts
};
